import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputBase,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Save, SlideshowOutlined } from '@mui/icons-material';
import { IconTile } from '../pagekit';
import { useApp } from '../../context/AppContext';
import { useOptionalActiveEditor } from '../../context/ActiveEditorContext';
import { useAuth } from '../../context/EnterpriseAuthContext';
import { supabase } from '../../lib/supabase';
import { registerUnsavedWorkFlusher } from '../../utils/navigation';
import UnsavedChangesDialog from '../powerPointRichTemplate/UnsavedChangesDialog';
import useUnsavedChangesGuard from '../powerPointRichTemplate/useUnsavedChangesGuard';
import { loadFullTemplate } from '../powerPointRichTemplate/templateRow';
import DeckWorkspace from './DeckWorkspace';
import { useDeckHistory } from './deckHistory';
import { renderStatusOf, useDeckSlideImages } from './deckImages';
import { deckCounts, parseDeckTemplate, serializeDeckTemplate, slidesOf } from './deckModel';
import { useDeckAutosave } from './useDeckAutosave';

/**
 * Everything a save writes, as one comparable string.
 * @param {string} name - Template name.
 * @param {string} description - Description.
 * @param {object} model - Deck template.
 * @returns {string} Snapshot.
 */
export function deckSnapshot(name, description, model) {
  return JSON.stringify([name, description, serializeDeckTemplate(model)]);
}

/**
 * Import notes to show: skipped slides and warnings from `generation_metadata`.
 * @param {object|null|undefined} metadata - Import metadata.
 * @returns {string[]} Notes.
 */
export function deckImportNotes(metadata) {
  if (!metadata || typeof metadata !== 'object') return [];
  const skipped = (Array.isArray(metadata.skippedSlides) ? metadata.skippedSlides : [])
    .filter((item) => item && Number.isInteger(item.index) && item.index >= 0)
    .map((item) => `Slide ${item.index + 1}: ${String(item.reason || 'skipped')}`);
  const warnings = (Array.isArray(metadata.warnings) ? metadata.warnings : [])
    .filter((w) => typeof w === 'string' && w.trim())
    .map((w) => w.trim());
  return [...new Set([...skipped, ...warnings])].slice(0, 12);
}

/** Screen-reader-only text. */
const VISUALLY_HIDDEN = { position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

/** An empty template: no render paths to sign (used when the harness supplies the images). */
const NO_IMAGES_MODEL = Object.freeze({ schema: 'quill.deck-template', version: 2, slides: [], shapes: [], fields: {} });

/**
 * What the save status announces to screen readers: only problems, and "Saved" after an explicit
 * Save. Routine autosave changes (unsaved, saving, saved) stay silent so they never talk over
 * what the user is typing; the visible chip still shows them.
 * @param {string} status - Autosave status.
 * @param {string} error - Last error.
 * @param {boolean} explicit - The user pressed Save.
 * @returns {string} Announcement.
 */
export function saveAnnouncement(status, error, explicit) {
  if (status === 'error') return `Not saved. ${error || ''}`.trim();
  if (status === 'conflict') return 'Not saved: this template changed elsewhere.';
  if (status === 'saved' && explicit) return 'Saved.';
  return '';
}

/**
 * Save state chip in the header.
 * @param {object} props - Props.
 * @param {string} props.status - Autosave status.
 * @param {string} [props.error] - Last error.
 * @param {boolean} [props.explicit] - The last save was the user's Save click.
 * @returns {React.ReactElement}
 */
function DeckSaveStatus({ status, error = '', explicit = false }) {
  const look = {
    saved: { label: 'Saved', color: 'success' },
    dirty: { label: 'Unsaved changes', color: 'default' },
    saving: { label: 'Saving…', color: 'default' },
    error: { label: 'Not saved', color: 'error' },
    conflict: { label: 'Changed elsewhere', color: 'warning' },
  }[status] || { label: 'Saved', color: 'success' };
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      color={look.color}
      label={look.label}
      data-testid="deck-save-status"
      tabIndex={status === 'error' ? 0 : undefined}
    />
  );
  return (
    <Box sx={{ display: 'inline-flex' }}>
      {status === 'error' && error ? <Tooltip title={`${error} Autosave is paused until your next change.`}>{chip}</Tooltip> : chip}
      <Box role="status" aria-live="polite" data-testid="deck-save-announcement" sx={VISUALLY_HIDDEN}>{saveAnnouncement(status, error, explicit)}</Box>
    </Box>
  );
}

/**
 * Editor for a v2 deck template (`content.schema === 'quill.deck-template'`): the master's real
 * slides with every box outlined, suggestion review, autosave with expectedUpdatedAt and an
 * unsaved-changes guard. Mounted by the PowerPoint template route once the row is loaded.
 * @param {object} props - Props.
 * @param {string} props.templateId - Template id.
 * @param {object} props.initialRow - Loaded template (mapped row: name, description, content, status, updatedAt, generationMetadata).
 * @param {object} [props.client] - Supabase client (the dev harness passes a local stand-in).
 * @param {Object<string, {png: string|null, thumb: string|null}>} [props.imagesOverride] - Slide
 *   images to show instead of signing render paths (dev harness: local PNGs, no network).
 * @returns {React.ReactElement}
 */
function DeckTemplateEditor({ templateId, initialRow, client = supabase, imagesOverride = null }) {
  const navigate = useNavigate();
  const { actions } = useApp();
  const { state: authState } = useAuth();
  const accountId = authState?.activeAccount?.id || null;
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const initialModel = useMemo(() => parseDeckTemplate(initialRow?.content), [initialRow]);
  const editor = useDeckHistory(initialModel || { schema: 'quill.deck-template', version: 2, slides: [], shapes: [], fields: {} });
  const { model } = editor;
  const [name, setName] = useState(initialRow?.name || '');
  const [description] = useState(initialRow?.description || '');
  const [status, setStatus] = useState(initialRow?.status || 'ready');
  const [generationMetadata, setGenerationMetadata] = useState(initialRow?.generationMetadata ?? null);
  const updatedAtRef = useRef(initialRow?.updatedAt || null);
  const [leaving, setLeaving] = useState(false);
  const [nameError, setNameError] = useState('');

  const snapshot = useMemo(() => deckSnapshot(name, description, model), [name, description, model]);

  // A list entry whose content was loaded in a batch has no generation_metadata (where the render
  // paths and import notes live): read the full row once for it.
  const needsMetadata = initialRow?.generationMetadata === undefined;
  useEffect(() => {
    if (!needsMetadata) return undefined;
    let active = true;
    loadFullTemplate({ actions: actionsRef.current, client, templateId, accountId, force: true })
      .then((row) => {
        if (active && row) setGenerationMetadata(row.generationMetadata ?? null);
      })
      .catch((error) => console.warn('Could not read the import details:', error));
    return () => {
      active = false;
    };
  }, [needsMetadata, templateId, accountId, client]);

  /**
   * Write one snapshot with optimistic concurrency.
   * @param {string} value - Snapshot.
   * @param {{promote?: boolean, force?: boolean}} options - Promote a draft to ready; overwrite a newer row.
   * @returns {Promise<object>} Result for useDeckAutosave.
   */
  const write = useCallback(async (value, options = {}) => {
    const [savedName, savedDescription, content] = JSON.parse(value);
    if (!String(savedName).trim()) {
      setNameError('Name the template to save it.');
      return { ok: false, error: 'Name the template to save it.' };
    }
    setNameError('');
    const promote = options.promote && (status === 'draft' || status === 'failed');
    const result = await actionsRef.current.updateTemplate(
      templateId,
      {
        name: String(savedName).trim(),
        description: savedDescription,
        content,
        template_type: 'powerpoint',
        ...(promote ? { status: 'ready' } : {}),
      },
      { expectedUpdatedAt: updatedAtRef.current, force: Boolean(options.force), silent: true }
    );
    if (result?.conflict) return { conflict: true, data: result.data };
    if (!result || result.success === false) return { ok: false, error: result?.error || 'The template could not be saved.' };
    if (result.data?.updatedAt) updatedAtRef.current = result.data.updatedAt;
    if (promote) setStatus('ready');
    return { ok: true };
  }, [templateId, status]);

  const autosave = useDeckAutosave({ snapshot, write, enabled: Boolean(initialModel) });
  const { isDirty, saveNow, markSaved, clearConflict } = autosave;
  // "Saved" is announced only after the user's own Save click (see saveAnnouncement).
  const [explicitSave, setExplicitSave] = useState(false);
  useEffect(() => {
    if (autosave.status === 'dirty') setExplicitSave(false);
  }, [autosave.status]);

  // Logging out (which unmounts the page without a navigation) saves pending edits first.
  useEffect(() => registerUnsavedWorkFlusher(async () => (isDirty() ? saveNow() : true)), [isDirty, saveNow]);

  const blocker = useUnsavedChangesGuard(isDirty);
  const leaveBlocked = blocker.state === 'blocked';

  const handleSaveAndLeave = async () => {
    setLeaving(true);
    let saved = false;
    try {
      saved = await saveNow();
    } finally {
      setLeaving(false);
    }
    if (saved) blocker.proceed?.();
    else blocker.reset?.();
  };

  /** Conflict: replace the editor with the version saved elsewhere. */
  const loadTheirs = () => {
    const server = autosave.conflict || {};
    const theirs = parseDeckTemplate(server.content);
    if (!theirs) {
      // Their version is not a deck template any more: reload the page's loader.
      clearConflict();
      window.location.reload();
      return;
    }
    const theirName = server.name || name;
    updatedAtRef.current = server.updatedAt || updatedAtRef.current;
    editor.reset(theirs);
    setName(theirName);
    if (server.status) setStatus(server.status);
    if (server.generationMetadata !== undefined) setGenerationMetadata(server.generationMetadata);
    markSaved(deckSnapshot(theirName, description, theirs));
    clearConflict();
  };

  /** Conflict: overwrite the other version with this one. */
  const keepMine = async () => {
    clearConflict();
    await saveNow({ force: true, always: true });
  };

  const signed = useDeckSlideImages({ model: imagesOverride ? NO_IMAGES_MODEL : model, generationMetadata: imagesOverride ? null : generationMetadata, accountId, client });
  const images = imagesOverride || signed.images;
  const { error: imageError, refresh: refreshImages } = signed;
  const lastImageRetryRef = useRef(0);
  const handleImageError = useCallback(() => {
    // Signed URLs expire: sign again, at most every 30 s.
    const now = Date.now();
    if (now - lastImageRetryRef.current < 30000) return;
    lastImageRetryRef.current = now;
    refreshImages();
  }, [refreshImages]);

  const renderStatus = renderStatusOf(model, generationMetadata);
  const counts = useMemo(() => deckCounts(model), [model]);
  const slideCount = useMemo(() => slidesOf(model).length, [model]);

  // Inky's deck surface (src/assistant/surfaceBridge.js): register a read-only handle with
  // getMeta() { kind: 'deck' } and getDeckFocus(); no getBlocks / applyEditPlan, so Inky reads the
  // deck with get_deck_template and never sends block edits.
  const activeEditor = useOptionalActiveEditor();
  const deckInfoRef = useRef({});
  deckInfoRef.current = { templateId, name, slideCount, fieldCount: counts.fields };
  const focusRef = useRef({ slideKey: null, shapeKey: null, slideNumber: null });
  const handleFocusChange = useCallback((focus) => {
    focusRef.current = focus;
  }, []);
  const inkyHandleRef = useRef(null);
  if (!inkyHandleRef.current) {
    inkyHandleRef.current = {
      getMeta: () => ({ kind: 'deck', templateId: deckInfoRef.current.templateId, title: deckInfoRef.current.name || '', readOnly: false }),
      getDeckFocus: () => ({ ...focusRef.current, slideCount: deckInfoRef.current.slideCount, fieldCount: deckInfoRef.current.fieldCount }),
    };
  }
  const registerEditor = activeEditor?.registerEditor;
  const unregisterEditor = activeEditor?.unregisterEditor;
  const hasModel = Boolean(initialModel);
  useEffect(() => {
    if (!hasModel || typeof registerEditor !== 'function') return undefined;
    const ref = inkyHandleRef;
    registerEditor(ref);
    return () => unregisterEditor?.(ref);
  }, [hasModel, registerEditor, unregisterEditor]);
  const notes = useMemo(() => deckImportNotes(generationMetadata), [generationMetadata]);
  const needsPromotion = status === 'draft' || status === 'failed';

  if (!initialModel) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">This deck template could not be read. Try importing the PowerPoint again.</Alert>
      </Box>
    );
  }

  // A pending render is not picked up later (nothing re-renders an import), so say what is true:
  // the images are not available for this import, and a new import adds them.
  let imageNote = '';
  if (renderStatus === 'pending' && !imagesOverride) imageNote = 'Slide images are not available for this import.';
  else if (renderStatus === 'failed' && !imagesOverride) imageNote = 'The slide images could not be rendered.';
  else if (imageError) imageNote = imageError;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 'var(--q-page-sticky-top, 0px)',
          zIndex: 20,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 1, sm: 2 },
          py: 1,
          bgcolor: 'var(--q-surface)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tooltip title="Back to templates">
          <IconButton onClick={() => navigate('/templates')} aria-label="Back to templates">
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
          <IconTile size={36}><SlideshowOutlined /></IconTile>
        </Box>
        <Box sx={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <InputBase
            value={name}
            placeholder="Template name"
            onChange={(event) => setName(event.target.value)}
            inputProps={{ 'aria-label': 'Template name', maxLength: 200, 'aria-invalid': Boolean(nameError) }}
            sx={{
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              maxWidth: 480,
              '& input': { px: 0.75, py: '1px', ml: -0.75, borderRadius: '6px', border: '1px solid transparent' },
              '& input:hover': { borderColor: 'divider' },
              '& input:focus': { borderColor: 'var(--q-accent)' },
            }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, fontSize: 12.5, color: 'text.secondary' }}>
            <span>Templates</span>
            <span aria-hidden="true">·</span>
            <span>PowerPoint master</span>
            <span aria-hidden="true">·</span>
            <span>{slideCount} slide{slideCount === 1 ? '' : 's'}, {counts.fields} AI field{counts.fields === 1 ? '' : 's'}</span>
            {status === 'draft' && <Chip label="Draft" size="small" color="warning" />}
          </Box>
        </Box>
        <DeckSaveStatus status={autosave.status} error={autosave.error} explicit={explicitSave} />
        <Button
          onClick={() => {
            setExplicitSave(true);
            saveNow({ promote: true, always: needsPromotion });
          }}
          variant="contained"
          disabled={autosave.status === 'saving' || autosave.status === 'conflict'}
          startIcon={autosave.status === 'saving' ? <CircularProgress size={16} color="inherit" /> : <Save />}
          sx={{ borderRadius: 999 }}
        >
          {needsPromotion ? 'Save and mark ready' : 'Save'}
        </Button>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {nameError && <Alert severity="error">{nameError}</Alert>}
        {autosave.status === 'error' && !nameError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => saveNow({ always: true })}>Try again</Button>}>
            {autosave.error} Autosave is paused until your next change.
          </Alert>
        )}
        {status === 'draft' && (
          <Alert severity="info">
            Quill suggested AI fields, prompts and slide roles for this master. Review them, accept the ones you want, then mark the template ready.
          </Alert>
        )}
        {renderStatus === 'pending' && !imagesOverride && (
          <Alert severity="info" data-testid="deck-render-pending">
            Slide images are not available for this import. The outlines already show every box and the fields work. Import the master again to add the images.
          </Alert>
        )}
        {renderStatus === 'failed' && !imagesOverride && (
          <Alert severity="warning">The slide images could not be rendered. The outlines and fields still work. Import the master again to try once more.</Alert>
        )}
        {imageError && renderStatus !== 'pending' && (
          <Alert severity="warning" action={<Button color="inherit" size="small" onClick={refreshImages}>Try again</Button>}>{imageError}</Alert>
        )}
        {notes.length > 0 && (
          <Alert severity="warning" data-testid="deck-import-notes">
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {notes.map((note) => <li key={note}>{note}</li>)}
            </Box>
          </Alert>
        )}

        <DeckWorkspace editor={editor} images={images} imageNote={imageNote} onImageError={handleImageError} onFocusChange={handleFocusChange} />
      </Box>

      <UnsavedChangesDialog
        open={leaveBlocked}
        saving={leaving}
        canSave
        onSave={handleSaveAndLeave}
        onDiscard={() => blocker.proceed?.()}
        onStay={() => blocker.reset?.()}
      />
      <Dialog open={Boolean(autosave.conflict)} maxWidth="xs" fullWidth aria-labelledby="deck-conflict-title" aria-describedby="deck-conflict-body">
        <DialogTitle id="deck-conflict-title">This template changed elsewhere</DialogTitle>
        <DialogContent>
          <DialogContentText id="deck-conflict-body">
            Someone saved this template in another tab or by another person since you opened it. Your latest changes are not saved yet.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={loadTheirs}>Load their version</Button>
          <Button onClick={keepMine} variant="contained">Keep my version</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeckTemplateEditor;
